package info.rezident.app.pages.profile

import com.varabyte.kobweb.core.PageContext
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import info.rezident.app.core.AppTracking
import info.rezident.app.core.Logger
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.quiz.UserQuizRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.launch
import kotlin.js.Date

class ProfileViewModel {
    private val viewModelScope = CoroutineScope(Dispatchers.Default + SupervisorJob())

    private val _state = MutableStateFlow(ProfileState())
    val state: StateFlow<ProfileState> = _state.asStateFlow()

    fun loadProfile() {
        viewModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, errorMessage = null)

            try {
                // Load user data in parallel
                val firebaseUser = AuthRepository.firebaseUser.firstOrNull()
                val uid = firebaseUser?.uid

                if (uid == null) {
                    _state.value = _state.value.copy(
                        isLoading = false,
                        errorMessage = "Nu ești autentificat"
                    )
                    return@launch
                }

                // Get user document for email, dateCreated, academicYear
                val userDoc = Firebase.firestore.collection("users").document(uid).get()

                val email = firebaseUser.email
                    ?: userDoc.takeIf { it.exists }?.let { runCatching { it.get<String>("email") }.getOrNull() }
                val dateCreatedString =
                    userDoc.takeIf { it.exists }?.let { runCatching { it.get<String>("dateCreated") }.getOrNull() }
                val dateCreatedLong =
                    userDoc.takeIf { it.exists }?.let { runCatching { it.get<Long>("dateCreated") }.getOrNull() }
                val dateCreatedDouble =
                    userDoc.takeIf { it.exists }?.let { runCatching { it.get<Double>("dateCreated") }.getOrNull() }
                val dateCreatedTimestamp =
                    userDoc.takeIf { it.exists }?.let { runCatching { it.get<Timestamp>("dateCreated") }.getOrNull() }
                val academicYear =
                    userDoc.takeIf { it.exists }?.let { runCatching { it.get<String>("academicYear") }.getOrNull() }

                // Load completed quizzes
                val quizzesLoadSuccess = UserQuizRepository.fetchUserQuizzes()
                val quizzes = if (quizzesLoadSuccess) {
                    UserQuizRepository.userQuizzes.value
                } else {
                    emptyList()
                }

                // Derive display name and role
                val displayName = firebaseUser.displayName?.takeIf { it.isNotBlank() }

                val initials = displayName?.take(2) ?: "💊"

                val roleText = deriveRoleFromAcademicYear(academicYear)

                val memberSince = when {
                    dateCreatedString != null -> formatMemberSinceDate(dateCreatedString)
                    dateCreatedLong != null -> formatMemberSinceDate(dateCreatedLong.toString())
                    dateCreatedDouble != null -> formatMemberSinceDate(dateCreatedDouble.toString())
                    dateCreatedTimestamp != null -> {
                        val ms =
                            (dateCreatedTimestamp.seconds * 1000.0) + (dateCreatedTimestamp.nanoseconds / 1_000_000.0)
                        formatMemberSinceDate(ms.toString())
                    }

                    else -> null
                }

                _state.value = ProfileState(
                    isLoading = false,
                    displayName = displayName ?: "Viitor Dr.",
                    initials = initials,
                    roleText = roleText,
                    email = email,
                    memberSince = memberSince,
                    academicYear = academicYear,
                    quizzes = quizzes,
                    completedCount = quizzes.size
                )

            } catch (e: Exception) {
                Logger.error("Error loading profile: ${e.message}")
                _state.value = _state.value.copy(
                    isLoading = false,
                    errorMessage = "Eroare la încărcarea profilului"
                )
            }
        }
    }

    fun retryLoadProfile() {
        loadProfile()
    }

    fun onQuizClicked(quizId: String, pageContext: PageContext) {
        AppTracking.logEvent(AppTracking.EVENT_PROFILE_QUIZ_CLICK, mapOf(AppTracking.PARAM_TEMPLATE_ID to 1))
        pageContext.router.navigateTo("/recomandari?quizId=$quizId")
    }

    fun onStartNewQuizClicked(pageContext: PageContext) {
        AppTracking.logEvent(AppTracking.EVENT_PROFILE_QUIZ_NEW)
        pageContext.router.navigateTo("/quiz")
    }

    private fun deriveRoleFromAcademicYear(academicYear: String?): String? {
        return when (academicYear) {
            "Anul 1", "Anul 2", "Anul 3", "Anul 4", "Anul 5", "Anul 6" -> "Student la medicină"
            "Sunt deja la Rezidențiat" -> "Medic Rezident"
            "Încă nu am început facultatea" -> "Pre-universitar"
            else -> null
        }
    }

    private fun formatMemberSinceDate(isoString: String): String {
        return try {
            val parsed: Date? = run {
                val d = Date(isoString)
                val t: Double = d.getTime()
                if (!t.isNaN()) d else null
            } ?: run {
                val numeric: Double? = isoString.toDoubleOrNull()
                if (numeric != null) {
                    val ms: Double = if (numeric < 1_000_000_000_000.0) numeric * 1000.0 else numeric
                    Date(ms)
                } else null
            }

            if (parsed == null) return "Dată necunoscută"

            val day: String = parsed.getDate().toString().padStart(2, '0')
            val month: String = (parsed.getMonth() + 1).toString().padStart(2, '0')
            val year: Int = parsed.getFullYear()
            "$day ${monthNumberToString(month)} $year"
        } catch (e: Throwable) {
            "Dată necunoscută"
        }
    }

    private fun monthNumberToString(month: String) =
        when (month) {
            "01" -> "ianuarie"
            "02" -> "februarie"
            "03" -> "martie"
            "04" -> "aprilie"
            "05" -> "mai"
            "06" -> "iunie"
            "07" -> "iulie"
            "08" -> "august"
            "09" -> "septembrie"
            "10" -> "octombrie"
            "11" -> "noiembrie"
            "12" -> "decembrie"
            else -> "-"
        }
}
